import { useEffect, useState } from 'react';
import SimulationModal from './SimulationModal';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
  Card,
  Checkbox,
  Stack,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
const statusLabels = {
  calm: 'רגוע',
  medium: 'בינוני',
  busy: 'עמוס',
  very_busy: 'עמוס מאוד',
};
const numberValue = (value) => (value === null || value === undefined ? '' : value);
const GpsLoadSettingsPage = ({ settings, onSave, loading }) => {
  const [draft, setDraft] = useState(settings);
  const [simulationBehaviorIndex, setSimulationBehaviorIndex] = useState(null);
  useEffect(() => {
    setDraft(settings);
  }, [settings]);
  if (!draft)
    return (
      <Stack
        sx={{
          gap: '22px',
          direction: 'rtl',
        }}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 'bold',
          }}
        >
          הגדרות עומס GPS
        </Typography>
      </Stack>
    );
  const updateRange = (index, field, value) => {
    setDraft((current) => ({
      ...current,
      movementRanges: current.movementRanges.map((range, rangeIndex) =>
        rangeIndex === index
          ? {
              ...range,
              [field]: value === '' ? null : Number(value),
            }
          : range,
      ),
    }));
  };
  const updateBehavior = (index, patch) => {
    setDraft((current) => ({
      ...current,
      behaviorTypes: current.behaviorTypes.map((behavior, behaviorIndex) =>
        behaviorIndex === index
          ? {
              ...behavior,
              ...patch,
            }
          : behavior,
      ),
    }));
  };
  const updateRule = (behaviorIndex, ruleIndex, patch) => {
    setDraft((current) => ({
      ...current,
      behaviorTypes: current.behaviorTypes.map((behavior, index) =>
        index === behaviorIndex
          ? {
              ...behavior,
              rules: behavior.rules.map((rule, indexOfRule) =>
                indexOfRule === ruleIndex
                  ? {
                      ...rule,
                      ...patch,
                    }
                  : rule,
              ),
            }
          : behavior,
      ),
    }));
  };
  const moveRule = (behaviorIndex, ruleIndex, direction) => {
    const targetIndex = ruleIndex + direction;
    const rules = [...(draft.behaviorTypes[behaviorIndex].rules || [])];
    if (targetIndex < 0 || targetIndex >= rules.length) return;
    [rules[ruleIndex], rules[targetIndex]] = [rules[targetIndex], rules[ruleIndex]];
    updateBehavior(behaviorIndex, {
      rules,
    });
  };
  const openSimulation = (behaviorIndex) => {
    const behavior = draft.behaviorTypes[behaviorIndex];
    if (!(behavior.simulationScenarios || []).length) {
      const automaticScenarios = (behavior.rules || []).map((rule, index) => ({
        id: `${behavior.key}-auto-${index + 1}`,
        name: `${statusLabels[rule.status] || 'תרחיש'} · תנאי ${index + 1}`,
        waitingPercent: Number(rule.minWaitingPercent),
        durationMinutes: Number(rule.minDurationMinutes),
        walkingPercent: 0,
      }));
      updateBehavior(behaviorIndex, {
        simulationScenarios: automaticScenarios,
      });
    }
    setSimulationBehaviorIndex(behaviorIndex);
  };
  const addBehavior = () => {
    const suffix = draft.behaviorTypes.length + 1;
    setDraft((current) => ({
      ...current,
      behaviorTypes: [
        ...current.behaviorTypes,
        {
          key: `NEW_BEHAVIOR_${suffix}`,
          googlePriority: 50,
          label: 'אופי מקום חדש',
          enabled: true,
          inferable: true,
          googleTypes: [],
          rules: [
            {
              status: 'calm',
              minWaitingPercent: 0,
              minDurationMinutes: 0,
              score: 3,
              reason: 'CONFIGURED_GPS_RULE',
            },
          ],
          boosts: [],
        },
      ],
    }));
  };
  const removeBehavior = (index) => {
    const behavior = draft.behaviorTypes[index];
    const count = draft.usageByBehavior?.[behavior.key] || 0;
    if (count > 0) {
      updateBehavior(index, {
        enabled: false,
      });
      return;
    }
    setDraft((current) => ({
      ...current,
      behaviorTypes: current.behaviorTypes.filter((_, behaviorIndex) => behaviorIndex !== index),
    }));
  };
  return (
    <Stack
      sx={{
        gap: '20px',
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '4px',
          direction: 'rtl',
        }}
      >
        <Stack>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 'bold',
            }}
          >
            הגדרות עומס GPS
          </Typography>
          <Typography>גרסה {draft.version || 1} · השינויים יחולו על דיווחים חדשים</Typography>
        </Stack>

        <Button disabled={loading} onClick={() => onSave(draft)}>
          שמירת הגדרות
        </Button>
      </Stack>

      <Stack
        sx={(theme) => ({
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '16px',
          padding: '20px',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Typography>סיווג תנועה</Typography>
        <Typography
          sx={(theme) => ({
            color: 'var(--muted)',
            [theme.breakpoints.down('lg')]: {
              margin: '4px 0 0',
              fontSize: '12px',
              display: 'none',
            },
            margin: '2px 0 0',
            fontSize: '11px',
          })}
        >
          הטווחים חייבים להיות רציפים, להתחיל ב־0 ולהסתיים ללא גבול עליון.
        </Typography>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(160px, 1fr))',
            gap: '12px',
            marginTop: '16px',
            [theme.breakpoints.down('md')]: {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },
            [theme.breakpoints.down('sm')]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          {draft.movementRanges.map((range, index) => (
            <Card
              key={range.key}
              sx={{
                display: 'grid',
                gap: '10px',
                padding: '14px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                background: '#f8fafc',
                '& label': {
                  display: 'grid',
                  gap: '5px',
                  fontSize: '13px',
                },
              }}
            >
              <Typography>{range.label}</Typography>
              <Stack>
                מ־קמ״ש
                <TextField
                  type="number"
                  min="0"
                  step="0.1"
                  value={numberValue(range.minKmh)}
                  onChange={(event) => updateRange(index, 'minKmh', event.target.value)}
                  fullWidth
                />
              </Stack>
              <Stack>
                עד קמ״ש
                <TextField
                  type="number"
                  min="0"
                  step="0.1"
                  disabled={index === draft.movementRanges.length - 1}
                  placeholder="ללא הגבלה"
                  value={numberValue(range.maxKmh)}
                  onChange={(event) => updateRange(index, 'maxKmh', event.target.value)}
                  fullWidth
                />
              </Stack>
            </Card>
          ))}
        </Stack>
      </Stack>

      <Stack
        sx={(theme) => ({
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: '16px',
          padding: '20px',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <Stack>
            <Typography>אופי מקום וכללי עומס</Typography>
            <Typography
              sx={(theme) => ({
                color: 'var(--muted)',
                [theme.breakpoints.down('lg')]: {
                  margin: '4px 0 0',
                  fontSize: '12px',
                  display: 'none',
                },
                margin: '2px 0 0',
                fontSize: '11px',
              })}
            >
              הכלל הראשון שמתאים לאחוז ההמתנה ולמשך הביקור קובע את הציון.
            </Typography>
          </Stack>
          <Button
            type="button"
            onClick={addBehavior}
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            הוספת אופי מקום
          </Button>
        </Stack>

        <Stack
          sx={{
            display: 'grid',
            gap: '16px',
            marginTop: '18px',
          }}
        >
          {draft.behaviorTypes.map((behavior, behaviorIndex) => {
            const usageCount = draft.usageByBehavior?.[behavior.key] || 0;
            return (
              <Accordion
                key={`${behavior.key}-${behaviorIndex}`}
                disableGutters
                sx={{
                  border: '1px solid #dbe3ee',
                  borderRadius: '14px !important',
                  backgroundColor: 'background.paper',
                  overflow: 'hidden',
                  boxShadow: 'none',
                  '&:before': {
                    display: 'none',
                  },
                  '& + &': {
                    mt: 2,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 2,
                    py: 0.5,
                    minHeight: 64,
                    '& .MuiAccordionSummary-content': {
                      alignItems: 'center',
                      gap: 2,
                      my: 1,
                    },
                  }}
                >
                  <Stack>
                    <Typography
                      sx={{
                        color: behavior.enabled ? 'success.main' : 'error.main',
                        fontWeight: 'bold',
                      }}
                    >
                      {behavior.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11,
                      }}
                    >
                      {`${behavior.key} (${usageCount} מקומות)`}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    display: 'grid',
                    gap: 2,
                    p: 2,
                    borderTop: '1px solid #cfd0d1',
                  }}
                >
                  <Stack
                    sx={{
                      justifyContent: 'flex-start',
                      gap: '12px',
                      flexWrap: 'wrap',
                      paddingTop: '16px',
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: 'space-between',
                      }}
                    >
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          gap: '7px',
                          minHeight: '34px',
                          padding: '9px 10px',
                          border: '1px solid #e7ebf0',
                          borderRadius: '9px',
                          background: '#fbfcfd',
                          color: 'var(--text)',
                          width: 'auto',
                          justifyContent: 'center',
                        }}
                      >
                        <Checkbox
                          checked={behavior.inferable}
                          onChange={(event) =>
                            updateBehavior(behaviorIndex, {
                              inferable: event.target.checked,
                            })
                          }
                        />
                        ניתן לחישוב מ־GPS
                      </Stack>

                      <Stack
                        direction="row"
                        sx={{
                          gap: '12px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Stack>
                          קוד
                          <TextField
                            value={behavior.key}
                            disabled={usageCount > 0 || behavior.key === 'UNKNOWN'}
                            onChange={(event) =>
                              updateBehavior(behaviorIndex, {
                                key: event.target.value.toUpperCase(),
                              })
                            }
                            fullWidth
                          />
                        </Stack>

                        <Stack>
                          שם
                          <TextField
                            value={behavior.label}
                            onChange={(event) =>
                              updateBehavior(behaviorIndex, {
                                label: event.target.value,
                              })
                            }
                            fullWidth
                          />
                        </Stack>
                      </Stack>
                    </Stack>

                    <Stack
                      direction="row"
                      sx={{
                        gap: '12px',
                      }}
                    >
                      <Button
                        disabled={!behavior.inferable || !(behavior.rules || []).length}
                        title={
                          !behavior.inferable ? 'אופי זה אינו ניתן לחישוב מ-GPS' : 'פתיחת סימולציה'
                        }
                        onClick={() => openSimulation(behaviorIndex)}
                        sx={{
                          background: '#edf2f7',
                          color: 'var(--text)',
                          '&:hover': {
                            background: '#ddebff',
                            color: '#1269e8',
                          },
                        }}
                      >
                        סימולציה
                      </Button>

                      <Button
                        onClick={() =>
                          updateBehavior(behaviorIndex, {
                            enabled: !behavior.enabled,
                          })
                        }
                        sx={String(behavior.enabled ? 'danger-outline' : 'secondary')
                          .split(/\s+/)
                          .map(
                            (name) =>
                              ({
                                secondary: {
                                  background: '#edf2f7',
                                  color: 'var(--text)',
                                  '&:hover': {
                                    background: '#ddebff',
                                    color: '#1269e8',
                                  },
                                },
                                'danger-outline': {
                                  color: '#b42318',
                                  borderColor: '#f1b9b4',
                                  background: '#fff5f4',
                                },
                              })[name],
                          )
                          .filter(Boolean)}
                      >
                        {behavior.enabled ? 'הפוך ללא פעיל' : 'הפוך לפעיל'}
                      </Button>
                    </Stack>

                    {behavior.key !== 'UNKNOWN' && usageCount === 0 && (
                      <Button
                        type="button"
                        onClick={() => removeBehavior(behaviorIndex)}
                        sx={{
                          color: '#b42318',
                          borderColor: '#f1b9b4',
                          background: '#fff5f4',
                        }}
                      >
                        הסרה
                      </Button>
                    )}
                  </Stack>

                  <Stack
                    sx={{
                      '& textarea': {
                        width: '100%',
                        resize: 'vertical',
                      },
                    }}
                  >
                    סוגי Google משויכים — מופרדים בפסיקים
                    <TextField
                      rows="2"
                      value={(behavior.googleTypes || []).join(', ')}
                      onChange={(event) =>
                        updateBehavior(behaviorIndex, {
                          googleTypes: event.target.value
                            .split(',')
                            .map((item) => item.trim())
                            .filter(Boolean),
                        })
                      }
                      multiline
                      fullWidth
                    />
                  </Stack>

                  {behavior.inferable && (
                    <>
                      <Stack
                        sx={{
                          display: 'grid',
                          gap: '8px',
                          overflowX: 'auto',
                        }}
                      >
                        <Stack
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '130px 145px 130px 90px minmax(180px, 1fr) 150px',
                            gap: '8px',
                            alignItems: 'center',
                            minWidth: '800px',
                            fontSize: '12px',
                            color: '#64748b',
                            fontWeight: '700',
                          }}
                        >
                          <Typography>מצב</Typography>
                          <Typography>מינימום המתנה %</Typography>
                          <Typography>מינימום זמן במקום</Typography>
                          <Typography>ציון</Typography>
                          <Typography>סיבה</Typography>
                          <Typography />
                        </Stack>
                        {(behavior.rules || []).map((item, ruleIndex) => (
                          <Stack
                            key={`${behavior.key}-${ruleIndex}`}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns:
                                '130px 145px 130px 90px minmax(180px, 1fr) 150px',
                              gap: '8px',
                              alignItems: 'center',
                              minWidth: '800px',
                              '& select': {
                                minWidth: '0',
                              },
                            }}
                          >
                            {' '}
                            <Typography
                              sx={String(`rule-status rule-status-${item.status}`)
                                .split(/\s+/)
                                .map(
                                  (name) =>
                                    ({
                                      'rule-status': {
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minHeight: '36px',
                                        padding: '5px 10px',
                                        borderRadius: '999px',
                                        fontSize: '13px',
                                        fontWeight: '800',
                                      },
                                      'rule-status-calm': {
                                        background: '#dcfae6',
                                        color: '#067647',
                                      },
                                      'rule-status-medium': {
                                        background: '#fff6d6',
                                        color: '#b54708',
                                      },
                                      'rule-status-busy': {
                                        background: '#ffead5',
                                        color: '#c4320a',
                                      },
                                      'rule-status-very_busy': {
                                        background: '#fee4e2',
                                        color: '#b42318',
                                      },
                                    })[name],
                                )
                                .filter(Boolean)}
                            >
                              {statusLabels[item.status] || 'לא ידוע'}
                            </Typography>
                            <TextField
                              type="number"
                              min="0"
                              max="100"
                              value={item.minWaitingPercent}
                              onChange={(event) =>
                                updateRule(behaviorIndex, ruleIndex, {
                                  minWaitingPercent: Number(event.target.value),
                                })
                              }
                              fullWidth
                            />
                            <TextField
                              type="number"
                              min="0"
                              value={item.minDurationMinutes}
                              onChange={(event) =>
                                updateRule(behaviorIndex, ruleIndex, {
                                  minDurationMinutes: Number(event.target.value),
                                })
                              }
                              fullWidth
                            />
                            <TextField
                              type="number"
                              min="1"
                              max="10"
                              value={item.score}
                              onChange={(event) => {
                                const score = Number(event.target.value);
                                const status =
                                  score <= 4
                                    ? 'calm'
                                    : score <= 6
                                      ? 'medium'
                                      : score <= 8
                                        ? 'busy'
                                        : 'very_busy';
                                updateRule(behaviorIndex, ruleIndex, {
                                  score,
                                  status,
                                });
                              }}
                              fullWidth
                            />
                            <TextField
                              value={item.reason || ''}
                              onChange={(event) =>
                                updateRule(behaviorIndex, ruleIndex, {
                                  reason: event.target.value,
                                })
                              }
                              fullWidth
                            />
                            <Stack
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '34px 34px 1fr',
                                gap: '5px',
                                '& button': {
                                  minHeight: '36px',
                                  padding: '4px 8px',
                                },
                              }}
                            >
                              <Button
                                type="button"
                                aria-label="העבר תנאי למעלה"
                                title="העבר למעלה"
                                disabled={ruleIndex === 0}
                                onClick={() => moveRule(behaviorIndex, ruleIndex, -1)}
                                sx={{
                                  background: '#edf2f7',
                                  color: 'var(--text)',
                                  '&:hover': {
                                    background: '#ddebff',
                                    color: '#1269e8',
                                  },
                                }}
                              >
                                ↑
                              </Button>
                              <Button
                                type="button"
                                aria-label="העבר תנאי למטה"
                                title="העבר למטה"
                                disabled={ruleIndex === behavior.rules.length - 1}
                                onClick={() => moveRule(behaviorIndex, ruleIndex, 1)}
                                sx={{
                                  background: '#edf2f7',
                                  color: 'var(--text)',
                                  '&:hover': {
                                    background: '#ddebff',
                                    color: '#1269e8',
                                  },
                                }}
                              >
                                ↓
                              </Button>
                              <Button
                                type="button"
                                onClick={() =>
                                  updateBehavior(behaviorIndex, {
                                    rules: behavior.rules.filter((_, index) => index !== ruleIndex),
                                  })
                                }
                                sx={{
                                  color: '#b42318',
                                  borderColor: '#f1b9b4',
                                  background: '#fff5f4',
                                }}
                              >
                                הסרה
                              </Button>
                            </Stack>
                          </Stack>
                        ))}
                      </Stack>
                      <Button
                        type="button"
                        onClick={() =>
                          updateBehavior(behaviorIndex, {
                            rules: [
                              ...(behavior.rules || []),
                              {
                                status: 'calm',
                                minWaitingPercent: 0,
                                minDurationMinutes: 0,
                                score: 3,
                                reason: 'CONFIGURED_GPS_RULE',
                              },
                            ],
                          })
                        }
                        sx={{
                          background: '#edf2f7',
                          color: 'var(--text)',
                          '&:hover': {
                            background: '#ddebff',
                            color: '#1269e8',
                          },
                        }}
                      >
                        הוספת כלל
                      </Button>
                    </>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Stack>
      </Stack>
      {simulationBehaviorIndex !== null && draft.behaviorTypes[simulationBehaviorIndex] && (
        <SimulationModal
          behavior={draft.behaviorTypes[simulationBehaviorIndex]}
          onChange={(simulationScenarios) =>
            updateBehavior(simulationBehaviorIndex, {
              simulationScenarios,
            })
          }
          onClose={() => setSimulationBehaviorIndex(null)}
        />
      )}
    </Stack>
  );
};
export default GpsLoadSettingsPage;
